/**
 * Users Hooks - React hooks for user management
 * Epic 2: User CRUD Operations
 */

import {
  createUser,
  deleteUser,
  getUserById,
  getUsers,
  updateUser,
  type UserFilters,
  type UserListResponse,
  type UserResponse,
} from '@/api/users';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useState } from 'react';

// ============================================
// TYPES
// ============================================

interface UseUsersResult {
  users: UserResponse[];
  total: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseUserResult {
  user: UserResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseUserMutationsResult {
  createUser: typeof createUser;
  updateUser: typeof updateUser;
  deleteUser: typeof deleteUser;
  isLoading: boolean;
  error: string | null;
}

// ============================================
// HOOKS
// ============================================

/**
 * Hook to fetch list of users with filters
 */
export function useUsers(filters: UserFilters = {}): UseUsersResult {
  const { t } = useTranslation();
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response: UserListResponse = await getUsers(filters);
      setUsers(response.data);
      setTotal(response.pagination.total);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('error.users.loadAll');
      setError(message);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    t,
    filters.search,
    filters.role,
    filters.status,
    filters.locationId,
    filters.limit,
    filters.offset,
  ]);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    total,
    isLoading,
    error,
    refetch: fetchUsers,
  };
}

/**
 * Hook to fetch single user by ID
 */
export function useUser(id: string | undefined): UseUserResult {
  const { t } = useTranslation();
  const [user, setUser] = useState<UserResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUser = useCallback(async () => {
    if (!id) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await getUserById(id);
      setUser(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('error.users.loadOne');
      setError(message);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    void fetchUser();
  }, [fetchUser]);

  return {
    user,
    isLoading,
    error,
    refetch: fetchUser,
  };
}

/**
 * Hook for user mutations (create, update, delete)
 */
export function useUserMutations(): UseUserMutationsResult {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wrappedCreate: typeof createUser = useCallback(
    async data => {
      setIsLoading(true);
      setError(null);
      try {
        return await createUser(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('error.users.create');
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const wrappedUpdate: typeof updateUser = useCallback(
    async (id, data) => {
      setIsLoading(true);
      setError(null);
      try {
        return await updateUser(id, data);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('error.users.update');
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const wrappedDelete: typeof deleteUser = useCallback(
    async id => {
      setIsLoading(true);
      setError(null);
      try {
        return await deleteUser(id);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('error.users.delete');
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  return {
    createUser: wrappedCreate,
    updateUser: wrappedUpdate,
    deleteUser: wrappedDelete,
    isLoading,
    error,
  };
}
